import { h } from "./../../utils/createElement";
import { __assign, __extends } from "tslib";
import * as React from 'react';
import ReactDOM from 'react-dom';
import { castInto, emptyFn } from '../../utils/helper';
import Trigger from '../../utils/Trigger';
import { consumeTheme } from '../../utils/theme';
import { decideKeepDom } from '../../utils/keepDom';
import { checkPositionChange } from '../../utils/checkPositionChange';
var TOOLTIP_VERTICAL_GAP = 8;
var TOOLTIP_HORIZONTAL_GAP = 4;
var ARROW_ADDITONAL_HORIZONTAL_GAP = 2;
var VIEWPORT_MARGIN = 2;
var TOOLTIP_TRANSITION_DURATION = 200;
var defaultMetrics = {
    position: { left: '0px', top: '0px' },
    effectivePlacement: 'bottom',
};
var arrowTransforms = {
    top: 'translate(-50%, -50%) rotate(225deg)',
    bottom: 'translate(-50%, 50%) rotate(45deg)',
    left: 'translate(-50%, -50%) rotate(135deg)',
    right: 'translate(50%, -50%) rotate(-45deg)',
};
var Tooltip = (function (_super) {
    __extends(Tooltip, _super);
    function Tooltip() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.rootRef = React.createRef();
        _this.overlayContainerRef = React.createRef();
        _this.forceRenderDom = false;
        _this.cancelPositionChangeChecker = null;
        _this.state = __assign({ currentVisible: !!_this.props.defaultVisible }, defaultMetrics);
        _this.show = function () {
            if (_this.props.disabled) {
                return;
            }
            _this.forceRenderDom = true;
            _this.setState(defaultMetrics, function () {
                _this.runVisibleEffects();
                _this.setStateVisible(true);
                _this.forceRenderDom = false;
            });
        };
        _this.hide = function () {
            clearTimeout(_this.showTimeout);
            if (!_this.isVisible()) {
                return;
            }
            _this.setStateVisible(false);
        };
        _this.toggle = function () {
            if (_this.props.disabled) {
                return;
            }
            if (_this.isVisible()) {
                _this.hide();
            }
            else {
                _this.show();
            }
        };
        _this.handleClickAnchor = function () {
            var _a = _this.props, trigger = _a.trigger, hideOnClick = _a.hideOnClick;
            if (trigger === 'click') {
                _this.toggle();
            }
            else if (trigger === 'hover' && hideOnClick) {
                _this.hide();
            }
        };
        _this.handleMouseDownAnchor = function () {
            var _a = _this.props, trigger = _a.trigger, hideOnMouseDown = _a.hideOnMouseDown;
            if (hideOnMouseDown && trigger === 'hover') {
                _this.hide();
            }
        };
        _this.handleMouseEnter = function () {
            clearTimeout(_this.hideTimeout);
            clearTimeout(_this.showTimeout);
            _this.showTimeout = setTimeout(_this.show, typeof _this.props.hoverDelay === 'object' ? _this.props.hoverDelay.show : _this.props.hoverDelay);
        };
        _this.handleMouseLeave = function () {
            clearTimeout(_this.showTimeout);
            _this.hideTimeout = setTimeout(_this.hide, typeof _this.props.hoverDelay === 'object' ? _this.props.hoverDelay.hide : _this.props.hoverDelay);
        };
        _this.themedRender = function (classNames) {
            var _a;
            var _b = _this.props, className = _b.className, style = _b.style, prefixCls = _b.prefixCls, children = _b.children, overlayClassName = _b.overlayClassName, overlayStyle = _b.overlayStyle, trigger = _b.trigger, offset = _b.offset, zIndex = _b.zIndex, containerClassName = _b.containerClassName, containerStyle = _b.containerStyle, destroyOnClose = _b.destroyOnClose, overlayHoverable = _b.overlayHoverable, containerDom = _b.containerDom;
            var effectivePlacement = _this.state.effectivePlacement;
            var wrapperCls = classNames(prefixCls, className, prefixCls + "-wrapper");
            var overlayCls = classNames(prefixCls + "-content", overlayClassName);
            var overlayStyleWithOffset = __assign(__assign({}, overlayStyle), { transform: "translate" + (effectivePlacement === 'top' || effectivePlacement === 'bottom' ? 'X' : 'Y') + "(" + offset + "px)" });
            var visible = _this.isVisible();
            var overlayContainerCls = classNames(prefixCls + "-container", containerClassName, (_a = {},
                _a[prefixCls + "-container-visible"] = visible,
                _a));
            var shouldDestroyDom = !_this.forceRenderDom && destroyOnClose && !decideKeepDom.call(_this, TOOLTIP_TRANSITION_DURATION, visible);
            var hoverTarget = '';
            if (trigger === 'hover') {
                hoverTarget = overlayHoverable ? 'outer' : 'inner';
            }
            return (h(Trigger, { className: wrapperCls, style: style, action: _this.hide, disabled: trigger !== 'click' },
                h("div", { onMouseEnter: hoverTarget === 'outer' ? _this.handleMouseEnter : undefined, onMouseLeave: hoverTarget === 'outer' ? _this.handleMouseLeave : undefined, ref: _this.rootRef },
                    h("div", { onClick: _this.handleClickAnchor, onMouseDown: _this.handleMouseDownAnchor, onMouseEnter: hoverTarget === 'inner' ? _this.handleMouseEnter : undefined, onMouseLeave: hoverTarget === 'inner' ? _this.handleMouseLeave : undefined }, children),
                    shouldDestroyDom ? null : ReactDOM.createPortal(h("div", { className: overlayContainerCls, ref: _this.overlayContainerRef, style: __assign(__assign({ zIndex: zIndex }, containerStyle), _this.state.position) },
                        h("div", { className: overlayCls, style: overlayStyleWithOffset, "aria-atomic": true, "aria-live": "assertive" }, _this.renderTitle(classNames)),
                        _this.renderArrow(classNames)), containerDom || document.body))));
        };
        return _this;
    }
    Tooltip.getDerivedStateFromProps = function (props, state) {
        if (props.disabled) {
            return { currentVisible: false };
        }
        return null;
    };
    Tooltip.prototype.componentDidMount = function () {
        if (this.isVisible()) {
            this.runVisibleEffects();
        }
    };
    Tooltip.prototype.componentWillUnmount = function () {
        var _a;
        (_a = this.cancelPositionChangeChecker) === null || _a === void 0 ? void 0 : _a.call(this);
        clearTimeout(this.showTimeout);
        clearTimeout(this.hideTimeout);
    };
    Tooltip.prototype.componentDidUpdate = function (prevProps) {
        var _a;
        if (!prevProps.visible && this.props.visible) {
            this.runVisibleEffects();
        }
        if (!this.isVisible()) {
            (_a = this.cancelPositionChangeChecker) === null || _a === void 0 ? void 0 : _a.call(this);
        }
    };
    Tooltip.prototype.isVisible = function () {
        var _a = this.props, forceVisible = _a.visible, disabled = _a.disabled;
        if (disabled) {
            return false;
        }
        var stateVisible = this.state.currentVisible;
        return typeof forceVisible === 'boolean' ? forceVisible : stateVisible;
    };
    Tooltip.prototype.setStateVisible = function (currentVisible) {
        this.setState({ currentVisible: currentVisible });
        this.props.onVisibleChange(currentVisible);
    };
    Tooltip.prototype.runVisibleEffects = function () {
        var _this = this;
        this.setState(this.calcOverlayMetrics(), function () {
            requestAnimationFrame(function () {
                var _a;
                (_a = _this.cancelPositionChangeChecker) === null || _a === void 0 ? void 0 : _a.call(_this);
                _this.cancelPositionChangeChecker = checkPositionChange(_this.rootRef.current, _this.hide);
            });
        });
    };
    Tooltip.prototype.calcStartPositionByAlignment = function (parentStart, parentEnd, selfLength) {
        var alignment = this.props.alignment;
        switch (alignment) {
            case 'start':
                return parentStart;
            case 'center':
                return (parentStart + parentEnd - selfLength) / 2;
            case 'end':
                return parentEnd - selfLength;
            default:
                return 0;
        }
    };
    Tooltip.prototype.calcOverlayMetrics = function () {
        var rootDom = this.rootRef.current;
        var containerDom = this.overlayContainerRef.current;
        if (!rootDom || !containerDom) {
            return defaultMetrics;
        }
        var rootRect = rootDom.getBoundingClientRect();
        var containerRect = containerDom.getBoundingClientRect();
        var placement = this.props.placement;
        if (typeof placement === 'string') {
            return {
                position: this.calcOverlayPositionByPlacement(rootRect, containerRect, placement).position,
                effectivePlacement: placement,
            };
        }
        for (var _i = 0, _a = placement; _i < _a.length; _i++) {
            var tryPlacement = _a[_i];
            var _b = this.calcOverlayPositionByPlacement(rootRect, containerRect, tryPlacement), position = _b.position, canFitIn = _b.canFitIn;
            if (canFitIn) {
                return { position: position, effectivePlacement: tryPlacement };
            }
        }
        return {
            position: this.calcOverlayPositionByPlacement(rootRect, containerRect, placement[0]).position,
            effectivePlacement: placement[0]
        };
    };
    Tooltip.prototype.calcOverlayPositionByPlacement = function (rootRect, containerRect, placement) {
        var leftX = rootRect.left, rightX = rootRect.right, topY = rootRect.top, bottomY = rootRect.bottom;
        var width = containerRect.width, height = containerRect.height;
        var _a = this.props, containerOffsetX = _a.containerOffsetX, containerOffsetY = _a.containerOffsetY, showArrow = _a.showArrow;
        var resultLeft = containerOffsetX;
        var resultTop = containerOffsetY;
        var horizontalGap = TOOLTIP_HORIZONTAL_GAP + (showArrow ? ARROW_ADDITONAL_HORIZONTAL_GAP : 0);
        if (placement === 'bottom' || placement === 'top') {
            resultLeft += this.calcStartPositionByAlignment(leftX, rightX, width);
        }
        if (placement === 'left') {
            resultLeft += leftX - horizontalGap - width;
        }
        if (placement === 'right') {
            resultLeft += rightX + horizontalGap;
        }
        var finalLeft = castInto(resultLeft, [VIEWPORT_MARGIN, innerWidth - width - VIEWPORT_MARGIN]);
        if (placement === 'left' || placement === 'right') {
            resultTop += this.calcStartPositionByAlignment(topY, bottomY, height);
        }
        if (placement === 'top') {
            resultTop += topY - TOOLTIP_VERTICAL_GAP - height;
        }
        if (placement === 'bottom') {
            resultTop += bottomY + TOOLTIP_VERTICAL_GAP;
        }
        var finalTop = castInto(resultTop, [VIEWPORT_MARGIN, innerHeight - height - VIEWPORT_MARGIN]);
        return {
            position: { left: finalLeft + "px", top: finalTop + "px" },
            canFitIn: finalLeft === resultLeft && finalTop === resultTop,
        };
    };
    Tooltip.prototype.renderArrow = function (classNames) {
        var _a = this.props, arrowOffsetX = _a.arrowOffsetX, arrowOffsetY = _a.arrowOffsetY, showArrow = _a.showArrow, prefixCls = _a.prefixCls, arrowClassName = _a.arrowClassName, arrowStyle = _a.arrowStyle;
        if (!showArrow) {
            return null;
        }
        var effectivePlacement = this.state.effectivePlacement;
        var arrowCls = classNames(prefixCls + "-arrow", arrowClassName, prefixCls + "-arrow-" + effectivePlacement);
        var transform = "translate(" + arrowOffsetX + "px, " + arrowOffsetY + "px) " + arrowTransforms[effectivePlacement];
        return h("div", { className: arrowCls, style: __assign({ transform: transform }, arrowStyle) });
    };
    Tooltip.prototype.renderTitle = function (classNames) {
        var _a = this.props, prefixCls = _a.prefixCls, title = _a.title;
        if (Array.isArray(title) && title.every(function (line) { return typeof line === 'string'; })) {
            return (h("div", { className: classNames(prefixCls + "-title-multiline") }, title.map(function (line, index) { return h("div", { key: index }, line); })));
        }
        return title;
    };
    Tooltip.prototype.render = function () {
        return consumeTheme(this.themedRender);
    };
    return Tooltip;
}(React.Component));
export default Tooltip;
Tooltip.defaultProps = {
    prefixCls: 'dui-tooltip',
    title: '',
    placement: 'bottom',
    trigger: 'hover',
    onVisibleChange: emptyFn,
    disabled: false,
    offset: 0,
    containerOffsetX: 0,
    containerOffsetY: 0,
    destroyOnClose: true,
    alignment: 'center',
    hoverDelay: 400,
    hideOnClick: false,
    hideOnMouseDown: false,
    overlayHoverable: true,
    arrowOffsetX: 0,
    arrowOffsetY: 0,
};
